import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  ChevronRight,
  CalendarDays,
  Star,
  BookOpen,
  BookMarked,
  Zap,
  FlaskConical,
  Trophy,
} from "lucide-react";
import apiFetch from "../apiFetch";
import levelsData from "../data/levels.json";
import "../styles/dailyTasksPage.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

const TASK_LABELS = {
  synonymsGame:       "Synonyms Game",
  antonymsGame:       "Antonyms Game",
  vocabMatchingGame:  "Vocab Matching",
  spotWordGame:       "Spot the Word",
  comprehensionGame:  "Comprehension",
  mcq10Game:          "MCQ Master",
  rankedGame:         "Ranked Mode",
  bookReading:        "Book Reading",
  bookComprehension:  "Book Comprehension",
};

const TASK_ICONS = {
  vocab:         <Zap size={18} />,
  comprehension: <FlaskConical size={18} />,
  book:          <BookMarked size={18} />,
};

function getTierName(tier) {
  const t = levelsData.tiers?.[String(tier)];
  return t?.name ?? `Tier ${tier}`;
}

const TOPIC_GAMES = new Set(["spotWordGame", "comprehensionGame", "mcq10Game"]);

function TaskCard({ task, onStart }) {
  const done   = !!task.completed_at;
  const params = task.parameters_json || {};
  const label  = TASK_LABELS[task.game_key] || task.game_key;

  const tags = [
    params.tierName || (params.tier ? getTierName(params.tier) : null),
    TOPIC_GAMES.has(task.game_key) ? (params.topic || null) : null,
    params.bookTitle || null,
    params.chapterDesc ? `Ch: ${params.chapterDesc.slice(0, 40)}…` : null,
  ].filter(Boolean);

  const required = task.required_count ?? 1;
  const current  = task.current_count  ?? 0;
  const showProgress = !done && required > 1;
  const pct = Math.min(100, Math.round((current / required) * 100));

  return (
    <div className={`dt-card${done ? " dt-card--done" : ""}`}>
      <div className="dt-card-left">
        <span className={`dt-type-icon dt-type-icon--${task.task_type}`}>
          {TASK_ICONS[task.task_type] || <Star size={18} />}
        </span>
        <div className="dt-card-body">
          <p className="dt-card-label">{label}</p>
          {tags.length > 0 && (
            <div className="dt-card-tags">
              {tags.map(t => <span key={t} className="dt-tag">{t}</span>)}
            </div>
          )}
          {showProgress && (
            <div className="dt-progress">
              <div className="dt-progress-bar">
                <div className="dt-progress-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="dt-progress-text">{current}/{required}</span>
            </div>
          )}
        </div>
      </div>
      <div className="dt-card-right">
        {done ? (
          <span className="dt-badge dt-badge--done">
            <CheckCircle2 size={14} /> Done
          </span>
        ) : (
          <button className="dt-btn dt-btn--primary" onClick={() => onStart(task)}>
            {current > 0 ? "Continue" : "Start"} <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Navigation helper ─────────────────────────────────────────────────────────

function navigateToGame(task, navigate) {
  const params = task.parameters_json || {};
  const tier     = params.tier ?? parseInt(localStorage.getItem("difficulty_tier") || "1", 10);
  const tierName = params.tierName || getTierName(tier);
  const topic    = params.topic || "";

  sessionStorage.setItem("dt_params", JSON.stringify({ taskId: task.id }));
  sessionStorage.setItem("returnTo", "/daily-tasks");

  const base = { fromGameSelect: true };

  switch (task.game_key) {
    case "synonymsGame":
      navigate("/synonymsGame", { state: base });
      break;
    case "antonymsGame":
      navigate("/antonymsGame", { state: base });
      break;
    case "vocabMatchingGame":
      navigate("/vocabMatchingGame", { state: base });
      break;
    case "spotWordGame":
      navigate("/spotWordGame", { state: { ...base, tier, tierName, topicCategory: topic } });
      break;
    case "comprehensionGame":
      navigate("/comprehensionGame", { state: { ...base, tier, tierName, topicCategory: topic } });
      break;
    case "mcq10Game":
      navigate("/mcq10Game", { state: { ...base, tier, tierName, topicCategory: topic } });
      break;
    case "rankedGame":
      navigate("/rankedGame", { state: base });
      break;
    case "bookReading":
      navigate("/chapterReadingPage", {
        state: {
          bookId:       params.bookId,
          chapterId:    params.chapterId,
          bookTitle:    params.bookTitle || params.bookId,
          chapterTitle: `Chapter`,
          chapterDesc:  params.chapterDesc || "",
          chapterIndex: 0,
          totalChapters: (params.allChapters || []).length || 1,
          allChapters:  [],
          launchedFrom: "daily-task",
        },
      });
      break;
    case "bookComprehension":
      navigate("/bookBasedComprehensionGame", {
        state: {
          ...base,
          bookId:      params.bookId,
          chapterId:   params.chapterId,
          bookTitle:   params.bookTitle || params.bookId,
          chapterDesc: params.chapterDesc || "",
        },
      });
      break;
    default:
      navigate("/home");
  }
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DailyTasksPage() {
  const navigate = useNavigate();
  const [tasks,   setTasks]   = useState([]);
  const [status,  setStatus]  = useState("loading");
  const [reward,  setReward]  = useState(null);

  const fetchTasks = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await apiFetch("/api/daily-tasks");
      if (res.status === 401) { setStatus("unauth"); return; }
      if (!res.ok) { setStatus("error"); return; }
      const data = await res.json();
      setTasks(Array.isArray(data) ? data : []);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    document.title = "Daily Tasks — Owllet Reading Quest";
    fetchTasks();
  }, [fetchTasks]);

  const handleStart = (task) => {
    navigateToGame(task, navigate);
  };

  // Check for reward after returning from a game (dt_reward in sessionStorage)
  useEffect(() => {
    const raw = sessionStorage.getItem("dt_reward");
    if (raw) {
      try {
        setReward(JSON.parse(raw));
      } catch {}
      sessionStorage.removeItem("dt_reward");
    }
  }, []);

  // Refetch when page becomes visible (user returned from a game)
  useEffect(() => {
    const onFocus = () => fetchTasks();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchTasks]);

  if (status === "loading") {
    return (
      <div className="dt-page">
        <div className="dt-loading">Loading your daily tasks…</div>
      </div>
    );
  }
  if (status === "unauth") {
    return (
      <div className="dt-page">
        <div className="dt-loading">Please <Link to="/">log in</Link> first.</div>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="dt-page">
        <div className="dt-loading">
          Failed to load tasks.{" "}
          <button className="dt-btn dt-btn--ghost" onClick={fetchTasks}>Retry</button>
        </div>
      </div>
    );
  }

  const pending   = tasks.filter(t => !t.completed_at);
  const completed = tasks.filter(t =>  t.completed_at);
  const allDone   = tasks.length > 0 && pending.length === 0;

  const TASK_MINUTES = { vocab: 5, comprehension: 7, book: 10 };
  const totalMins = pending.reduce((sum, t) => sum + (TASK_MINUTES[t.task_type] ?? 5), 0);

  const tasksByType = (list) => ({
    vocab:         list.filter(t => t.task_type === "vocab"),
    comprehension: list.filter(t => t.task_type === "comprehension"),
    book:          list.filter(t => t.task_type === "book"),
  });

  const pendingGroups   = tasksByType(pending);
  const completedGroups = tasksByType(completed);

  return (
    <div className="dt-page">
      {/* Header */}
      <header className="dt-header">
        <div className="dt-header-left">
          <div className="dt-header-logo">
            <CalendarDays size={24} color="#fff" />
          </div>
          <div>
            <h1 className="dt-header-title">Daily Tasks</h1>
            <p className="dt-header-sub">
              {allDone
                ? "All done for today — great work!"
                : `${pending.length} task${pending.length !== 1 ? "s" : ""} remaining`}
            </p>
          </div>
        </div>
        <div className="dt-header-center">
          <p className="dt-header-motivate">Finish your tasks and level up your skills!</p>
        </div>
      </header>

      <main className="dt-main">

        {/* All-complete banner */}
        {allDone && (
          <div className="dt-banner dt-banner--success">
            <Trophy size={28} className="dt-banner-icon" />
            <div>
              <p className="dt-banner-title">All tasks complete!</p>
              <p className="dt-banner-sub">
                You earned{" "}
                <strong>25 coins</strong> and <strong>25 XP</strong>.
                New tasks will be waiting tomorrow.
              </p>
            </div>
          </div>
        )}

        {/* Pending tasks */}
        {pending.length > 0 && (
          <section className="dt-section">
            <h2 className="dt-section-title">
              <Clock size={22} /> Tasks for today!
              {totalMins > 0 && (
                <span className="dt-time-estimate">~{totalMins} min</span>
              )}
            </h2>

            {pendingGroups.vocab.length > 0 && (
              <div className="dt-group">
                <h3 className="dt-group-title">
                  <Zap size={15} /> Vocabulary
                </h3>
                {pendingGroups.vocab.map(t => (
                  <TaskCard key={t.id} task={t} onStart={handleStart} />
                ))}
              </div>
            )}

            {pendingGroups.comprehension.length > 0 && (
              <div className="dt-group">
                <h3 className="dt-group-title">
                  <FlaskConical size={15} /> Comprehension
                </h3>
                {pendingGroups.comprehension.map(t => (
                  <TaskCard key={t.id} task={t} onStart={handleStart} />
                ))}
              </div>
            )}

            {pendingGroups.book.length > 0 && (
              <div className="dt-group">
                <h3 className="dt-group-title">
                  <BookMarked size={15} /> Book
                </h3>
                {pendingGroups.book.map(t => (
                  <TaskCard key={t.id} task={t} onStart={handleStart} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Completed tasks */}
        {completed.length > 0 && (
          <section className="dt-section">
            <h2 className="dt-section-title dt-section-title--green">
              <CheckCircle2 size={18} /> Completed
            </h2>

            {completedGroups.vocab.length > 0 && (
              <div className="dt-group">
                <h3 className="dt-group-title">
                  <Zap size={15} /> Vocabulary
                </h3>
                {completedGroups.vocab.map(t => (
                  <TaskCard key={t.id} task={t} onStart={handleStart} />
                ))}
              </div>
            )}

            {completedGroups.comprehension.length > 0 && (
              <div className="dt-group">
                <h3 className="dt-group-title">
                  <FlaskConical size={15} /> Comprehension
                </h3>
                {completedGroups.comprehension.map(t => (
                  <TaskCard key={t.id} task={t} onStart={handleStart} />
                ))}
              </div>
            )}

            {completedGroups.book.length > 0 && (
              <div className="dt-group">
                <h3 className="dt-group-title">
                  <BookMarked size={15} /> Book
                </h3>
                {completedGroups.book.map(t => (
                  <TaskCard key={t.id} task={t} onStart={handleStart} />
                ))}
              </div>
            )}
          </section>
        )}

        {tasks.length === 0 && (
          <div className="dt-empty">
            <CalendarDays size={48} className="dt-empty-icon" />
            <p>No tasks found. Try refreshing.</p>
            <button className="dt-btn dt-btn--primary" onClick={fetchTasks}>
              Refresh
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
